import { useMemo, useState } from "react";
import { Form, Input, DatePicker, Drawer, Skeleton, Select } from "../../components/ui";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import {
  TreePalm,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Users,
} from "lucide-react";
import {
  useGetAllHolidays,
  useAddHoliday,
  useUpdateHoliday,
  useDeleteHoliday,
} from "../../query/holidayQuery";
import { useSaveTeacherDutyDay, useTeacherDutyDays } from "../../query/settingsQuery";
import { isOpenWeekend, isWeekendKey } from "../../utils/schoolTime";
import {
  holidaysByDate,
  holidaysInMonth,
  upcomingAfterMonth,
  formatHolidayRange,
  audienceLabel,
  audienceClassName,
  holidayAudience,
  HOLIDAY_AUDIENCES,
  EVENT_TYPES,
  eventType,
  eventTypeLabel,
  holidayStart,
  holidayDayCount,
  isHolidayStartDay,
  isHolidayEndDay,
} from "./holidayHelpers";
import "../teachers/teachers.css";
import "../teachers/teacherDetails.css";
import "./holidays.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const LESSON_TONES = ["is-navy", "is-green", "is-yellow", "is-blue"];

function asDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.format) return value.format("YYYY-MM-DD");
  return "";
}

function Holidays() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [cursor, setCursor] = useState(() => dayjs().startOf("month"));
  const [selectedDay, setSelectedDay] = useState(() => dayjs().startOf("day"));
  const [focusedHolidayId, setFocusedHolidayId] = useState(null);
  const [openDayOpen, setOpenDayOpen] = useState(false);
  const [openDay, setOpenDay] = useState({
    name: "",
    startKey: "",
    endKey: "",
    checkInClock: "12:00",
    checkOutClock: "16:00",
  });
  const [form] = Form.useForm();

  const { isLoading, isFetching, data: holidays = [] } = useGetAllHolidays();
  const { data: dutyDays = [] } = useTeacherDutyDays();
  const { mutate: saveDuty, isPending: savingDuty } = useSaveTeacherDutyDay();
  const { mutate: addHoliday, isPending: isAdding } = useAddHoliday();
  const { mutate: updateHoliday, isPending: isUpdating } = useUpdateHoliday();
  const { mutate: deleteHoliday } = useDeleteHoliday();

  const isMutating = isAdding || isUpdating;
  const isEdit = Boolean(editingHoliday);
  const today = dayjs().startOf("day");

  const dateMap = useMemo(() => holidaysByDate(holidays), [holidays]);

  const calendarDays = useMemo(() => {
    const start = cursor.startOf("month");
    const gridStart = start.subtract(start.day(), "day");
    return Array.from({ length: 42 }, (_, index) => gridStart.add(index, "day"));
  }, [cursor]);

  const monthHolidays = useMemo(() => holidaysInMonth(holidays, cursor), [holidays, cursor]);

  const upcoming = useMemo(
    () => upcomingAfterMonth(holidays, monthHolidays, today).slice(0, 8),
    [holidays, monthHolidays, today]
  );

  const focusHoliday = (item, focusDay = null, { jumpToMonth = false } = {}) => {
    const day = (focusDay ? dayjs(focusDay) : holidayStart(item)).startOf("day");
    if (!day.isValid()) return;
    if (jumpToMonth) {
      setCursor(day.startOf("month"));
    }
    setSelectedDay(day);
    setFocusedHolidayId(item._id);
  };

  const changeMonth = (direction) => {
    setCursor((value) => (direction === "prev" ? value.subtract(1, "month") : value.add(1, "month")));
    setFocusedHolidayId(null);
  };

  const openAdd = (presetDate = null) => {
    setEditingHoliday(null);
    form.resetFields();
    form.setFieldsValue({ audience: "BOTH", type: "HOLIDAY" });
    if (presetDate) {
      const day = dayjs(presetDate).startOf("day");
      form.setFieldsValue({ date: day, endDate: day, audience: "BOTH", type: "HOLIDAY" });
    }
    setDrawerOpen(true);
  };

  const openEdit = (item) => {
    setEditingHoliday(item);
    const start = holidayStart(item);
    form.setFieldsValue({
      name: item.name || "",
      type: eventType(item),
      audience: item.audience || "BOTH",
      date: start.isValid() ? start : null,
      endDate: item.endDate ? dayjs(item.endDate) : start.isValid() ? start : null,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingHoliday(null);
    form.resetFields();
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const start = asDateString(values.date);
      const end = asDateString(values.endDate || values.date);
      const payload = {
        name: values.name?.trim(),
        type: values.type || "HOLIDAY",
        audience: values.audience || "BOTH",
        date: start,
        endDate: end !== start ? end : undefined,
      };

      if (isEdit) {
        updateHoliday(
          { id: editingHoliday._id, ...payload },
          {
            onSuccess: () => {
              swal("Success", "Holiday updated successfully", "success");
              closeDrawer();
            },
            onError: (error) =>
              swal("Error", error?.message || "Failed to update holiday", "error"),
          }
        );
        return;
      }

      addHoliday(payload, {
        onSuccess: () => {
          swal("Success", "Holiday added successfully", "success");
          closeDrawer();
        },
        onError: (error) => swal("Error", error?.message || "Failed to add holiday", "error"),
      });
    });
  };

  const handleDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: `Delete "${item.name || "this holiday"}"?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteHoliday(item._id, {
        onSuccess: () => swal("Success", "Holiday deleted successfully", "success"),
        onError: (error) =>
          swal("Cannot delete", error?.message || "Failed to delete holiday", "error"),
      });
    });
  };

  const openOpenDay = (day) => {
    const weekend = day && (day.day() === 0 || day.day() === 6);
    const key = weekend ? day.format("YYYY-MM-DD") : "";
    setOpenDay({
      name: "",
      startKey: key,
      endKey: key,
      checkInClock: "12:00",
      checkOutClock: "16:00",
    });
    setOpenDayOpen(true);
  };

  const saveOpenDay = () => {
    const startKey = openDay.startKey;
    const endKey = openDay.endKey || startKey;
    if (!isWeekendKey(startKey)) {
      swal("Open day", "Choose a Saturday or Sunday.", "error");
      return;
    }
    const sameDay = endKey === startKey;
    const satToSun = dayjs(endKey).diff(dayjs(startKey), "day") === 1 && dayjs(startKey).day() === 6;
    if (!sameDay && !satToSun) {
      swal("Open day", "Use one weekend day, or Saturday through the next Sunday.", "error");
      return;
    }
    saveDuty(
      { ...openDay, endKey, name: openDay.name.trim() || "Open weekend" },
      {
        onSuccess: (response) => {
          const sent = Boolean(response?.data?.data?.noticeSent);
          swal(
            "Saved",
            sent
              ? "Teachers were told to come in, with the check-in time."
              : "That weekend is open for teachers. The notice could not be sent.",
            sent ? "success" : "warning"
          );
          setOpenDayOpen(false);
        },
        onError: (error) => swal("Error", error?.message || "Could not save the open day", "error"),
      }
    );
  };

  const goToday = () => {
    const now = dayjs();
    setCursor(now.startOf("month"));
    setSelectedDay(now.startOf("day"));
    setFocusedHolidayId(null);
  };

  const renderSideHoliday = (item, index, { jumpToMonth = false } = {}) => {
    const days = holidayDayCount(item);
    const isFocused = focusedHolidayId === item._id;
    const start = holidayStart(item);

    return (
      <article
        key={item._id}
        className={`td-lesson hol-side-link${isFocused ? " is-focused" : ""} ${LESSON_TONES[index % LESSON_TONES.length]}`}
        onClick={() => focusHoliday(item, null, { jumpToMonth })}
      >
        <div className="td-lesson-copy">
          <h4>{item.name || "—"}</h4>
          <p>{formatHolidayRange(item)}</p>
          <div className="td-lesson-meta">
            <span>
              <i>
                <Users size={13} />
              </i>
              {eventTypeLabel(item)} · {audienceLabel(holidayAudience(item))}
            </span>
            <span>
              <i>
                <Calendar size={13} />
              </i>
              {days > 1 ? `${days} days` : start.format("dddd")}
            </span>
          </div>
        </div>
        <span className="td-lesson-avatar" aria-hidden>
          {start.isValid() ? start.format("D") : "—"}
        </span>
        <div className="hol-side-icon-actions">
          <button
            type="button"
            className="hol-icon-btn is-edit"
            aria-label={`Edit ${item.name || "holiday"}`}
            onClick={(event) => {
              event.stopPropagation();
              openEdit(item);
            }}
          >
            <Pencil size={14} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="hol-icon-btn is-delete"
            aria-label={`Delete ${item.name || "holiday"}`}
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(item);
            }}
          >
            <Trash2 size={14} strokeWidth={2} />
          </button>
        </div>
      </article>
    );
  };

  return (
    <div className="data-page holidays-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <h2 className="hol-page-title">
            <span className="hol-page-title-icon" aria-hidden>
              <TreePalm size={18} />
            </span>
            School Holidays
          </h2>
        </div>

        <div className="data-toolbar-actions">
          <button type="button" className="hol-open-btn" onClick={() => openOpenDay(selectedDay)}>
            <Plus size={18} />
            Add open day
          </button>
          <button type="button" className="data-primary" onClick={() => openAdd(selectedDay)}>
            <Plus size={18} />
            Add Holiday
          </button>
        </div>
      </div>

      <div className="hol-layout">
        <main className="hol-main">
          <div className={`data-card hol-cal-card${isFetching || isMutating ? " is-fetching" : ""}`}>
            {isLoading && !holidays.length ? (
              <div style={{ padding: 24 }}>
                <Skeleton active paragraph={{ rows: 8 }} />
              </div>
            ) : (
              <>
                <div className="hol-cal-head">
                  <div className="hol-cal-nav">
                    <button
                      type="button"
                      aria-label="Previous month"
                      onClick={() => changeMonth("prev")}
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <strong>{cursor.format("MMMM YYYY")}</strong>
                    <button
                      type="button"
                      aria-label="Next month"
                      onClick={() => changeMonth("next")}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                  <button type="button" className="hol-cal-today" onClick={goToday}>
                    Today
                  </button>
                </div>

                <div className="hol-cal-weekdays">
                  {WEEKDAYS.map((label, index) => (
                    <span key={label} className={index === 0 || index === 6 ? "is-weekend" : ""}>
                      {label}
                    </span>
                  ))}
                </div>

                <div className="hol-cal-grid">
                  {calendarDays.map((day) => {
                    const key = day.format("YYYY-MM-DD");
                    const dayHolidays = dateMap[key] || [];
                    const inMonth = day.month() === cursor.month();
                    const isWeekend = day.day() === 0 || day.day() === 6;
                    const openWeekend = isOpenWeekend(key, dutyDays);
                    const isToday = day.isSame(today, "day");
                    const isSelected = day.isSame(selectedDay, "day");

                    return (
                      <button
                        key={key}
                        type="button"
                        className={`hol-cal-day${inMonth ? "" : " is-muted"}${openWeekend ? " is-open" : isWeekend ? " is-weekend" : ""}${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}${dayHolidays.length ? " has-holiday" : ""}`}
                        onClick={() => {
                          setSelectedDay(day.startOf("day"));
                          setFocusedHolidayId(null);
                        }}
                        onDoubleClick={() => openAdd(day)}
                      >
                        <span className="hol-cal-num">{day.date()}</span>
                        {openWeekend ? <span className="hol-open-tag">Open</span> : null}
                        <div className="hol-cal-events">
                          {dayHolidays.slice(0, 2).map((item) => {
                            const isStart = isHolidayStartDay(item, day);
                            const isEnd = isHolidayEndDay(item, day);
                            const isFocused = focusedHolidayId === item._id;
                            const audience = holidayAudience(item);
                            return (
                              <span
                                key={`${item._id}-${key}`}
                                className={`hol-cal-event ${audienceClassName(audience)}${isStart ? " is-start" : ""}${isEnd ? " is-end" : ""}${!isStart && !isEnd ? " is-span" : ""}${isFocused ? " is-focused" : ""}`}
                                title={`${item.name} · ${audienceLabel(audience)}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  focusHoliday(item, day);
                                }}
                                onDoubleClick={(event) => {
                                  event.stopPropagation();
                                  openEdit(item);
                                }}
                              >
                                {item.name}
                              </span>
                            );
                          })}
                          {dayHolidays.length > 2 ? (
                            <span className="hol-cal-more">+{dayHolidays.length - 2} more</span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="hol-cal-legend">
                  <div className="hol-cal-legend-items">
                    <span>
                      <i className="is-audience-both" /> Both
                    </span>
                    <span>
                      <i className="is-audience-student" /> Students
                    </span>
                    <span>
                      <i className="is-audience-teacher" /> Teachers
                    </span>
                  </div>
                  <span className="hol-cal-hint">Upcoming cards jump to date · double-click a day to add</span>
                </div>
              </>
            )}
          </div>
        </main>

        <aside className="td-schedule hol-side">
          <div className="td-schedule-head">
            <h3>This month</h3>
            <p>
              {cursor.format("MMMM YYYY")}
              {monthHolidays.length
                ? ` · ${monthHolidays.length} scheduled`
                : " · Nothing scheduled"}
            </p>
          </div>

          <div className={`td-schedule-list${monthHolidays.length === 0 ? " is-empty" : ""}`}>
            {monthHolidays.length ? (
              monthHolidays.map((item, index) => renderSideHoliday(item, index))
            ) : (
              <div className="hol-side-empty">
                <span className="hol-side-empty-icon">
                  <Calendar size={24} strokeWidth={1.6} />
                </span>
                <span>No holidays in {cursor.format("MMMM")}.</span>
              </div>
            )}
          </div>

          <div className="td-schedule-head hol-side-subhead">
            <h3>Upcoming</h3>
            <p>{upcoming.length ? "Next scheduled breaks" : "No upcoming holidays"}</p>
          </div>

          <div className={`td-schedule-list${upcoming.length === 0 ? " is-empty" : ""}`}>
            {upcoming.length ? (
              upcoming.map((item, index) => renderSideHoliday(item, index + 1, { jumpToMonth: true }))
            ) : (
              <div className="hol-side-empty">
                <span className="hol-side-empty-icon">
                  <TreePalm size={24} strokeWidth={1.6} />
                </span>
                <span>No upcoming holidays scheduled.</span>
              </div>
            )}
          </div>
        </aside>
      </div>

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        placement="right"
        title={isEdit ? "Edit Holiday" : "Add Holiday"}
        width={400}
        footer={
          <>
            <button
              type="button"
              className="bw-drawer-btn is-ghost"
              onClick={closeDrawer}
              disabled={isMutating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bw-drawer-btn is-primary hol-save-btn"
              onClick={handleSubmit}
              disabled={isMutating}
            >
              {isMutating ? "Saving..." : isEdit ? "Update" : "Save"}
            </button>
          </>
        }
      >
        <Form form={form} layout="vertical" className="hol-drawer-form" onFinish={handleSubmit}>
          <div className="hol-drawer-field">
            <p className="hol-drawer-label">
              Name<span>*</span>
            </p>
            <Form.Item
              name="name"
              className="hol-form-item"
              rules={[{ required: true, message: "Please enter a name" }]}
            >
              <Input placeholder="e.g. Christmas Break" />
            </Form.Item>
          </div>

          <div className="hol-drawer-field">
            <p className="hol-drawer-label">
              Type<span>*</span>
            </p>
            <Form.Item
              name="type"
              className="hol-form-item"
              initialValue="HOLIDAY"
              rules={[{ required: true, message: "Please choose a type" }]}
            >
              <Select placeholder="Holiday or event">
                {EVENT_TYPES.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="hol-drawer-field">
            <p className="hol-drawer-label">
              Applies to<span>*</span>
            </p>
            <Form.Item
              name="audience"
              className="hol-form-item"
              initialValue="BOTH"
              rules={[{ required: true, message: "Please select who this holiday applies to" }]}
            >
              <Select placeholder="Select audience">
                {HOLIDAY_AUDIENCES.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="hol-drawer-field">
            <p className="hol-drawer-label">
              Start date<span>*</span>
            </p>
            <Form.Item
              name="date"
              className="hol-form-item"
              rules={[{ required: true, message: "Please select a start date" }]}
            >
              <DatePicker placeholder="Start date" style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <div className="hol-drawer-field">
            <p className="hol-drawer-label">End date</p>
            <Form.Item
              name="endDate"
              className="hol-form-item"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue("date");
                    if (!value || !start) return Promise.resolve();
                    if (dayjs(value).startOf("day").valueOf() >= dayjs(start).startOf("day").valueOf()) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("End date cannot be before start date"));
                  },
                }),
              ]}
            >
              <DatePicker placeholder="End date (optional for single day)" style={{ width: "100%" }} />
            </Form.Item>
          </div>
        </Form>
      </Drawer>

      <Drawer
        open={openDayOpen}
        onClose={() => setOpenDayOpen(false)}
        placement="right"
        title="Add open day"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={() => setOpenDayOpen(false)} disabled={savingDuty}>
              Cancel
            </button>
            <button type="button" className="bw-drawer-btn is-primary hol-save-btn" onClick={saveOpenDay} disabled={savingDuty}>
              {savingDuty ? "Saving..." : "Save"}
            </button>
          </>
        }
      >
        <div className="hol-drawer-form">
          <p className="hol-drawer-help">
            Saturday or Sunday only. Teachers come in. Students stay home.
          </p>
          <label className="hol-drawer-field">
            <p className="hol-drawer-label">Name</p>
            <Input
              value={openDay.name}
              placeholder="Open Saturday"
              onChange={(event) => setOpenDay((current) => ({ ...current, name: event.target.value }))}
            />
          </label>
          <label className="hol-drawer-field">
            <p className="hol-drawer-label">
              Start<span>*</span>
            </p>
            <Input
              type="date"
              value={openDay.startKey}
              onChange={(event) => setOpenDay((current) => ({ ...current, startKey: event.target.value }))}
            />
          </label>
          <label className="hol-drawer-field">
            <p className="hol-drawer-label">End</p>
            <Input
              type="date"
              value={openDay.endKey}
              onChange={(event) => setOpenDay((current) => ({ ...current, endKey: event.target.value }))}
            />
          </label>
          <label className="hol-drawer-field">
            <p className="hol-drawer-label">
              Check-in<span>*</span>
            </p>
            <Input
              type="time"
              value={openDay.checkInClock}
              onChange={(event) => setOpenDay((current) => ({ ...current, checkInClock: event.target.value }))}
            />
          </label>
          <label className="hol-drawer-field">
            <p className="hol-drawer-label">
              Check-out<span>*</span>
            </p>
            <Input
              type="time"
              value={openDay.checkOutClock}
              onChange={(event) => setOpenDay((current) => ({ ...current, checkOutClock: event.target.value }))}
            />
          </label>
        </div>
      </Drawer>
    </div>
  );
}

export default Holidays;
